import { useCallback, useEffect, useState } from 'react'

export function useUnsavedChangesWarning(isDirty: boolean) {
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])
}

type UnsavedChangesGuardOptions = {
  isDirty: boolean
  isSubmitting?: boolean
  onOpenChange: (open: boolean) => void
}

export function useUnsavedChangesGuard({
  isDirty,
  isSubmitting = false,
  onOpenChange,
}: UnsavedChangesGuardOptions) {
  const [confirmOpen, setConfirmOpen] = useState(false)

  const requestClose = useCallback(() => {
    if (isSubmitting) return
    if (isDirty) {
      setConfirmOpen(true)
      return
    }
    onOpenChange(false)
  }, [isDirty, isSubmitting, onOpenChange])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        onOpenChange(true)
        return
      }
      requestClose()
    },
    [onOpenChange, requestClose],
  )

  const keepEditing = useCallback(() => {
    setConfirmOpen(false)
  }, [])

  const discardChanges = useCallback(() => {
    setConfirmOpen(false)
    onOpenChange(false)
  }, [onOpenChange])

  return {
    confirmOpen,
    setConfirmOpen,
    onOpenChange: handleOpenChange,
    requestClose,
    keepEditing,
    discardChanges,
  }
}
